import { Component, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { MatRadioModule } from '@angular/material/radio';
import { TranslateModule } from '@ngx-translate/core';
import { FieldType, FieldTypeConfig, FormlyModule } from '@ngx-formly/core';
import { firstErrorMessage } from '../../forms/formly-errors';

const SEG_BASE = 'px-4 py-2 border-0 rounded-full bg-transparent font-alternate text-[13px] font-medium cursor-pointer transition-colors';
const SEG_ACTIVE = 'bg-white text-mty-primary shadow-[0_1px_3px_rgba(19,32,52,0.1)]';
const SEG_IDLE = 'text-[var(--mat-sys-on-surface-variant)] hover:text-mty-primary';

@Component({
  selector: 'app-formly-mat-radio',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatRadioModule, TranslateModule, FormlyModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <fieldset
      class="flex flex-col gap-2.5 border-0 p-0 m-0"
      [attr.data-field-id]="field.id">
      @if (props.label) {
        <legend [class]="legendClass()">
          {{ props.label | translate }}
          @if (props.required) { <span class="text-mty-danger ml-0.5">*</span> }
        </legend>
      }
      @if (props['variant'] === 'segmented') {
        <div
          class="inline-flex p-1 rounded-full bg-mty-primary/5 border border-[var(--mat-sys-outline-variant)] gap-0.5 w-fit"
          role="radiogroup"
          [attr.aria-labelledby]="id + '-label'">
          @for (opt of radioOptions(); track opt.value) {
            <button
              type="button"
              [class]="segmentedClass(opt.value)"
              (click)="select(opt.value)">
              {{ opt.label | translate }}
            </button>
          }
        </div>
      } @else {
        <mat-radio-group
          [attr.aria-labelledby]="id + '-label'"
          [id]="id"
          [formControl]="formControl"
          [formlyAttributes]="field"
          color="primary"
          [class.flex-row]="(props['direction'] || 'row') === 'row'"
          [class.flex-col]="(props['direction'] || 'row') === 'column'"
          class="flex gap-6">
          @for (opt of radioOptions(); track opt.value) {
            <mat-radio-button [value]="opt.value">{{ opt.label | translate }}</mat-radio-button>
          }
        </mat-radio-group>
      }
      @if (formControl.invalid && (formControl.dirty || formControl.touched)) {
        <span class="inline-flex items-center gap-1 font-base text-xs text-mty-danger mt-1">
          {{ errorMessage() | translate }}
        </span>
      }
    </fieldset>
  `,
})
export class MatRadioFieldComponent extends FieldType<FieldTypeConfig> {
  radioOptions(): { value: unknown; label: string }[] {
    return Array.isArray(this.props.options) ? (this.props.options as { value: unknown; label: string }[]) : [];
  }

  select(value: unknown): void {
    this.formControl.setValue(value);
    this.formControl.markAsDirty();
    this.formControl.markAsTouched();
  }

  segmentedClass(value: unknown): string {
    return `${SEG_BASE} ${this.formControl.value === value ? SEG_ACTIVE : SEG_IDLE}`;
  }

  errorMessage(): string {
    return firstErrorMessage(this.formControl);
  }

  isInvalid(): boolean {
    return this.formControl.invalid && (this.formControl.dirty || this.formControl.touched);
  }

  legendClass(): string {
    const base = 'inline-block font-alternate text-xs font-semibold tracking-wider uppercase mb-2';
    return this.isInvalid()
      ? `${base} text-mty-danger`
      : `${base} text-[var(--mat-sys-on-surface-variant)]`;
  }
}
